import type {
  StudentConversationCreateOptionsResource,
  StudentConversationDetailResource,
  StudentConversationQuestionResource,
} from "api/generated";
import { mapAttachments, mapLinks, mapVideos } from "shared/mappers";
import type {
  StudentConversationCreateOptions,
  StudentConversationDetail,
  StudentConversationQuestion,
} from "conversations/types";
import { parseSchoolDateTime } from "shared/utils";

/**
 * Exported because the docent's detail payload carries the same list, read-only: one
 * gesprek's voorbereiding must map identically whichever side is looking at it.
 */
export const studentConversationQuestionFromDto = (
  dto: StudentConversationQuestionResource,
): StudentConversationQuestion => ({
  id: dto.id,
  question: dto.question,
  type: dto.type,
  options: dto.options ?? null,
  required: dto.required,
  order: dto.order,
  answer: dto.answer,
});

/**
 * Maps `GET api/my-conversations/{conversation}`.
 *
 * `date` carries a Dutch wall-clock moment, so it is read through the school clock rather
 * than the browser's timezone — same as `studentConversationRowFromDto`.
 *
 * Both notes columns arrive as null while nothing is written and are mapped to "": the
 * Gespreksnotitie block renders one as text and binds the other to a textarea, and neither
 * wants a null check for "not written yet".
 */
export const studentConversationDetailFromDto = (
  dto: StudentConversationDetailResource,
): StudentConversationDetail => ({
  id: dto.id,
  name: dto.name,
  date: parseSchoolDateTime(dto.date),
  endDate: parseSchoolDateTime(dto.end_date),
  conversationType: {
    id: dto.conversation_type.id,
    name: dto.conversation_type.name,
  },
  partner:
    dto.partner === null
      ? null
      : {
          name: dto.partner.name,
          avatar: dto.partner.avatar,
        },
  student: {
    name: dto.student.name,
    avatar: dto.student.avatar,
  },
  teacherShortage: dto.teacher_shortage ?? null,
  subjectShortage: dto.subject_shortage ?? null,
  isNote: dto.is_note,
  allowsPreparation: dto.allows_preparation,
  needsPreparation: dto.needs_preparation,
  isPrepared: dto.is_prepared,
  status: dto.status,
  preparedAt: parseSchoolDateTime(dto.prepared_at),
  description: dto.description ?? null,
  teacherNotes: dto.teacher_notes ?? "",
  studentNotes: dto.student_notes ?? "",
  teacherNotesUpdatedAt: parseSchoolDateTime(dto.teacher_notes_updated_at),
  studentNotesUpdatedAt: parseSchoolDateTime(dto.student_notes_updated_at),
  // Already ordered by the API; mapped straight through so the form renders the docent's
  // order without re-sorting.
  questions: dto.questions.map(studentConversationQuestionFromDto),
  attachments: mapAttachments(dto.attachments),
  links: mapLinks(dto.links),
  videos: mapVideos(dto.videos),
  attachmentAllowed: Boolean(dto.attachment_allowed),
  attachmentRequired: Boolean(dto.attachment_required),
  preparationAttachments: mapAttachments(dto.preparation_attachments),
  preparationLinks: mapLinks(dto.preparation_links),
  preparationVideos: mapVideos(dto.preparation_videos),
  invitationAttachments: mapAttachments(dto.invitation_attachments),
  invitationLinks: mapLinks(dto.invitation_links),
  invitationVideos: mapVideos(dto.invitation_videos),
});

/**
 * Maps `GET api/my-conversations/create-options` — what the "Gesprek vastleggen" modal may
 * offer. Already ordered by the API, so nothing is re-sorted here.
 */
export const studentConversationCreateOptionsFromDto = (
  dto: StudentConversationCreateOptionsResource,
): StudentConversationCreateOptions => ({
  conversationTypes: dto.conversation_types.map((type) => ({
    id: type.id,
    name: type.name,
  })),
  partners: dto.partners.map((partner) => ({
    id: partner.id,
    name: partner.name,
    shortage: partner.shortage ?? null,
    avatar: partner.avatar ?? null,
  })),
  subjects: dto.subjects.map((subject) => ({
    shortage: subject.shortage,
    name: subject.name,
  })),
});
